import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Dropdown } from 'primereact/dropdown';
import InfoCard from '../InfoCard/InfoCard';
import DataModal from '../DataModal/DataModal';
import { SectionType } from '../DataModal/DataModal.interface';
import ContentSectionSkeleton from '../SectionSkeletons/ContentSectionSkeleton';
import LossOfExclusivityDetailModal from '../LossOfExclusivitySection/components/LossOfExclusivityDetailModal/LossOfExclusivityDetailModal';
import SolicitationDetailModal, {
  SolicitationDetailsResponse,
} from '../SolicitationDetailModal/SolicitationDetailModal';
import { InfoCardData, ANDAInfoCardData } from 'Interfaces/ProductWikiInterfaces';
import { LossOfExclusivityInfoCardData } from 'Interfaces/ProductWikiInterfaces/landingPage.types';
import { getColumnsBySectionType } from '../DataModal/modalColumnConfigs';
import { useStore } from 'hooks/useStore';
import { getSolicitationDetails } from 'services/productWikiServices';
import { ANDADetailModal } from 'components/shared/ANDADetailModal';
import './ANDAUpdatesSection.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { ANDAUpdatesSectionProps, PeriodOption } from './ANDAUpdatesSection.types';

const ANDAUpdatesSection: React.FC<ANDAUpdatesSectionProps> = observer(({ andaUpdates }) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentSection, setCurrentSection] = useState<SectionType>('andaUpdates');
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [selectedAnda, setSelectedAnda] = useState<ANDAInfoCardData | null>(null);
  // LOE detail modal state
  const [showLoeDetailModal, setShowLoeDetailModal] = useState<boolean>(false);
  const [selectedLoeCard, setSelectedLoeCard] = useState<LossOfExclusivityInfoCardData | null>(
    null
  );
  // Solicitation detail modal state
  const [showSolicitationDetailModal, setShowSolicitationDetailModal] = useState<boolean>(false);
  const [solicitationDetails, setSolicitationDetails] =
    useState<SolicitationDetailsResponse | null>(null);
  const [isSolicitationDetailsLoading, setIsSolicitationDetailsLoading] = useState<boolean>(false);

  const {
    LandingPageStore: {
      fetchContractExpirations,
      fetchAndaUpdates,
      fetchLossOfExclusivity,
      fetchPreSolicitationAlerts,
      isContractExpirationsLoading,
      isAndaUpdatesLoading,
      isLossOfExclusivityLoading,
      isPreSolicitationAlertsLoading,
      landingPageData,
      selectedContractExpirationsPeriod,
      selectedAndaUpdatesPeriod,
      selectedLossOfExclusivityPeriod,
      selectedPreSolicitationAlertsFilter: _selectedPreSolicitationAlertsFilter,
    },
  } = useStore();

  const periodOptions: PeriodOption[] = [
    { label: 'Past 3 months', value: 90 },
    { label: 'Past 6 months', value: 180 },
    { label: 'Past 12 months', value: 365 },
  ];

  const handlePeriodChange = async (e: { value: number | string }) => {
    const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
    // Fetch ANDA updates with the selected period (store will update selectedAndaUpdatesPeriod)
    await fetchAndaUpdates(periodValue);
  };

  const handleContractPeriodChange = async (e: { value: number | string }) => {
    const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
    await fetchContractExpirations(periodValue);
  };

  const handleLoePeriodChange = async (e: { value: number | string }) => {
    const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
    await fetchLossOfExclusivity(periodValue);
  };

  const _handlePsaFilterChange = async (e: { value: number | string }) => {
    const filterValue = e.value as string;
    const apiType = filterValue === 'duedates' ? 'duedate' : 'deadline';
    await fetchPreSolicitationAlerts(apiType);
  };

  // Get loading state for current section
  const getCurrentSectionLoading = (): boolean => {
    switch (currentSection) {
      case 'contractExpirations':
        return isContractExpirationsLoading;
      case 'andaUpdates':
        return isAndaUpdatesLoading;
      case 'lossOfExclusivity':
        return isLossOfExclusivityLoading;
      case 'preSolicitationAlerts':
        return isPreSolicitationAlertsLoading;
      default:
        return false;
    }
  };

  const displayedUpdates = andaUpdates.slice(0, 6);
  const hasMoreUpdates = andaUpdates.length > 6;

  // Get data for current section
  const getSectionData = (section: SectionType): InfoCardData[] => {
    if (!landingPageData) return [];

    switch (section) {
      case 'contractExpirations':
        return landingPageData.contractExpirations || [];
      case 'andaUpdates':
        return landingPageData.andaUpdates || [];
      case 'preSolicitationAlerts':
        return landingPageData.preSolicitationAlerts || [];
      case 'lossOfExclusivity':
        return landingPageData.lossOfExclusivity || [];
      // case 'marketIntelligence':
      //   return landingPageData.marketIntelligence || [];
      default:
        return [];
    }
  };

  // Handle section change in modal
  const handleSectionChange = (section: SectionType) => {
    setCurrentSection(section);
  };

  // Get columns for current section
  const columns = getColumnsBySectionType(currentSection);
  const modalData = getSectionData(currentSection);

  // Handle ANDA card click to open detail modal
  const handleAndaCardClick = (anda: ANDAInfoCardData) => {
    setSelectedAnda(anda);
    setShowDetailModal(true);
  };

  // Close ANDA detail modal
  const handleDetailModalHide = () => {
    setShowDetailModal(false);
    setSelectedAnda(null);
  };

  // Handle LOE card click to open detail modal (for when user switches to LOE section)
  const handleLoeCardClick = (item: LossOfExclusivityInfoCardData) => {
    setSelectedLoeCard(item);
    setShowLoeDetailModal(true);
  };

  // Handle Solicitation card click to fetch details and open modal
  const handleSolicitationCardClick = async (item: InfoCardData) => {
    const solicitationNumber = item.solicitation_number;
    if (!solicitationNumber) {
      console.error('No solicitation number available for this item');
      return;
    }

    setIsSolicitationDetailsLoading(true);
    setShowSolicitationDetailModal(true);

    try {
      const response = await getSolicitationDetails(solicitationNumber);
      setSolicitationDetails(response.data);
    } catch (error) {
      console.error('Error fetching solicitation details:', error);
      setSolicitationDetails(null);
    } finally {
      setIsSolicitationDetailsLoading(false);
    }
  };

  // Handle row click based on current section
  const handleRowClick = (row: InfoCardData | ANDAInfoCardData | LossOfExclusivityInfoCardData) => {
    if (currentSection === 'andaUpdates') {
      handleAndaCardClick(row as ANDAInfoCardData);
    } else if (currentSection === 'lossOfExclusivity') {
      handleLoeCardClick(row as LossOfExclusivityInfoCardData);
    } else if (currentSection === 'preSolicitationAlerts') {
      handleSolicitationCardClick(row as InfoCardData);
    }
  };

  return (
    <>
      <section className="content-section">
        <div className="section-header">
          <div className="section-title">
            <i className="fas fa-file-medical" style={{ color: 'var(--success-color)' }}></i>
            Recent ANDA Updates
            <span className="section-badge">
              {andaUpdates.length} in{' '}
              {periodOptions.find(option => option.value === selectedAndaUpdatesPeriod)?.label}
            </span>
          </div>
          <div className="period-selector">
            <Dropdown
              value={selectedAndaUpdatesPeriod}
              options={periodOptions}
              onChange={handlePeriodChange}
              placeholder="Select Period"
              className="period-dropdown"
              appendTo={typeof document !== 'undefined' ? document.body : undefined}
            />
          </div>
        </div>
        <div className="section-content">
          {isAndaUpdatesLoading ? (
            <ContentSectionSkeleton />
          ) : andaUpdates.length === 0 ? (
            <div className="no-data-message">No data available for Recent ANDA Updates</div>
          ) : (
            <>
              <div className="three-card-grid">
                {displayedUpdates.map((update, index) => (
                  <div key={index} className="three-card-wrapper">
                    <InfoCard
                      {...update}
                      cardColor={update.color}
                      url={update.link || undefined}
                      onCardClick={() => handleAndaCardClick(update)}
                    />
                  </div>
                ))}
              </div>
              {hasMoreUpdates && (
                <div className="see-more-container">
                  <button className="see-more-btn" onClick={() => setShowModal(true)}>
                    See More
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <DataModal
        visible={showModal}
        onHide={() => {
          setShowModal(false);
          setCurrentSection('andaUpdates'); // Reset to default section when closing
        }}
        data={modalData}
        sectionType={currentSection}
        columns={columns}
        onSectionChange={handleSectionChange}
        sectionDropdownConfigs={{
          contractExpirations: {
            value: selectedContractExpirationsPeriod,
            options: periodOptions,
            onChange: handleContractPeriodChange,
            placeholder: 'Select Period',
          },
          andaUpdates: {
            value: selectedAndaUpdatesPeriod,
            options: periodOptions,
            onChange: handlePeriodChange,
            placeholder: 'Select Period',
          },
          lossOfExclusivity: {
            value: selectedLossOfExclusivityPeriod,
            options: periodOptions,
            onChange: handleLoePeriodChange,
            placeholder: 'Select Period',
          },
        }}
        isLoading={getCurrentSectionLoading()}
        onRowClick={
          currentSection === 'andaUpdates' ||
          currentSection === 'lossOfExclusivity' ||
          currentSection === 'preSolicitationAlerts'
            ? handleRowClick
            : undefined
        }
      />

      {/* ANDA Detail Modal */}
      <ANDADetailModal
        visible={showDetailModal}
        onHide={handleDetailModalHide}
        data={selectedAnda}
      />

      {/* LOE Detail Modal - for when user switches to LOE section */}
      <LossOfExclusivityDetailModal
        visible={showLoeDetailModal}
        onHide={() => {
          setShowLoeDetailModal(false);
          setSelectedLoeCard(null);
        }}
        data={selectedLoeCard}
      />

      {/* Solicitation Detail Modal - for when user switches to Solicitation Alerts section */}
      <SolicitationDetailModal
        visible={showSolicitationDetailModal}
        onHide={() => {
          setShowSolicitationDetailModal(false);
          setSolicitationDetails(null);
        }}
        data={solicitationDetails}
        isLoading={isSolicitationDetailsLoading}
      />
    </>
  );
});

export default ANDAUpdatesSection;
